import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Save } from 'lucide-react'
import toast from 'react-hot-toast'

import { PageHeader } from '@/components/shared/PageHeader'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { showError, showSuccess } from '@/lib/swal'
import { useAuthStore } from '@/store/auth.store'
import { overTimeRequestApiService } from '@/services/overTimeRequestApiService'
import type { OverTimeRequestVm, OverTimeStatusLookupItem } from '@/api/types'

function todayIso() {
  return new Date().toISOString().slice(0, 10)
}

function dateOnlyToIso(dateOnly: string): string {
  const d = new Date(`${dateOnly}T00:00:00.000Z`)
  return d.toISOString()
}

function dateAndTimeToIso(dateOnly: string, hhmm: string): string {
  const d = new Date(`${dateOnly}T${hhmm}:00`)
  return d.toISOString()
}

function durationHoursToIso(dateOnly: string, hours: number): string {
  const base = new Date(`${dateOnly}T00:00:00.000Z`).getTime()
  const ms = Math.max(0, Math.round(hours * 60 * 60 * 1000))
  return new Date(base + ms).toISOString()
}

function calcHours(dateOnly: string, from: string, to: string): number {
  if (!dateOnly || !from || !to) return 0
  const start = new Date(`${dateOnly}T${from}:00`)
  let end = new Date(`${dateOnly}T${to}:00`)
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return 0
  if (end.getTime() < start.getTime()) end = new Date(end.getTime() + 24 * 60 * 60 * 1000)
  const diffMs = end.getTime() - start.getTime()
  const hours = diffMs / (1000 * 60 * 60)
  if (!Number.isFinite(hours) || hours < 0) return 0
  return Math.round(hours * 100) / 100
}

export const OvertimeRequestDetail: React.FC = () => {
  const { t } = useTranslation('common')
  const navigate = useNavigate()
  const params = useParams<{ id: string }>()
  const { user } = useAuthStore()
  const branchId = Number(user?.selectedBranch?.branchId ?? 0)

  const isNew = params.id === 'new' || !params.id
  const editId = !isNew ? Number(params.id) : 0

  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [statusLookups, setStatusLookups] = useState<OverTimeStatusLookupItem[]>([])

  const [overtimeDate, setOvertimeDate] = useState<string>(todayIso())
  const [timeFrom, setTimeFrom] = useState<string>('')
  const [timeTo, setTimeTo] = useState<string>('')
  const [details, setDetails] = useState<string>('')

  const [status, setStatus] = useState<number>(0)
  const [approvalDate, setApprovalDate] = useState<string>('')
  const [isEditable, setIsEditable] = useState<number>(0)

  const totalHours = useMemo(() => calcHours(overtimeDate, timeFrom, timeTo), [overtimeDate, timeFrom, timeTo])

  const req = t('overtimeRequestPage.requiredMark')
  const readOnly = !isNew && (Number(isEditable ?? 0) === 0 || Number(status ?? 0) === 435)

  useEffect(() => {
    let cancelled = false
    void overTimeRequestApiService
      .getStatusLookups()
      .then((list) => {
        if (!cancelled) setStatusLookups(list || [])
      })
      .catch(() => {
        if (!cancelled) setStatusLookups([])
      })
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    if (isNew) return
    if (!branchId || !editId) return
    let cancelled = false
    setLoading(true)
    void overTimeRequestApiService
      .getById(editId, branchId)
      .then((vm) => {
        if (cancelled) return
        if (!vm) {
          toast.error(t('overtimeRequestPage.loadFailed'))
          navigate('/self-services/overtime-requests')
          return
        }
        setOvertimeDate(String(vm.overTimeDate || '').slice(0, 10) || todayIso())
        setTimeFrom(String(vm.timeFrom || '').slice(11, 16))
        setTimeTo(String(vm.timeTo || '').slice(11, 16))
        setDetails(vm.details ?? '')
        setStatus(Number(vm.status ?? 0))
        setApprovalDate(String(vm.approvalDate || '').slice(0, 19))
        setIsEditable(Number(vm.isEditable ?? 0))
      })
      .catch((e: any) => {
        if (!cancelled) toast.error(e?.message ?? t('overtimeRequestPage.loadFailed'))
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [isNew, editId, branchId, navigate, t])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!branchId) return

    if (!overtimeDate) {
      await showError(t('overtimeRequestPage.saveFailedTitle'), t('overtimeRequestPage.toastDate'))
      return
    }
    if (!timeFrom) {
      await showError(t('overtimeRequestPage.saveFailedTitle'), t('overtimeRequestPage.toastTimeFrom'))
      return
    }
    if (!timeTo) {
      await showError(t('overtimeRequestPage.saveFailedTitle'), t('overtimeRequestPage.toastTimeTo'))
      return
    }
    if (totalHours <= 0) {
      await showError(t('overtimeRequestPage.saveFailedTitle'), t('overtimeRequestPage.toastInvalidTimeRange'))
      return
    }

    try {
      setSaving(true)
      const vm: OverTimeRequestVm = {
        id: isNew ? 0 : editId,
        empId: 0,
        overTimeDate: dateOnlyToIso(overtimeDate),
        timeFrom: dateAndTimeToIso(overtimeDate, timeFrom),
        timeTo: dateAndTimeToIso(overtimeDate, timeTo),
        totalHours: durationHoursToIso(overtimeDate, totalHours),
        amount: 0,
        details: details.trim() || '',
        status: isNew ? 0 : Number(status || 0),
        approvalDate: approvalDate ? new Date(approvalDate).toISOString() : isNew ? new Date().toISOString() : '',
        isEditable,
        branchId,
      }

      try {
        console.groupCollapsed('[OverTimeRequestDetail] Save payload')
        console.log(vm)
        console.log('json', JSON.stringify(vm, null, 2))
        console.groupEnd()
      } catch {
        // ignore
      }

      if (isNew) await overTimeRequestApiService.create(vm)
      else await overTimeRequestApiService.update(vm)

      await showSuccess(
        isNew ? t('overtimeRequestPage.savedTitle') : t('overtimeRequestPage.updatedTitle'),
        isNew ? t('overtimeRequestPage.savedMessage') : t('overtimeRequestPage.updatedMessage'),
        2500,
      )
      navigate('/self-services/overtime-requests')
    } catch (err: any) {
      try {
        console.groupCollapsed('[OverTimeRequestDetail] Save error (raw)')
        console.log(err)
        console.log('message', err?.message)
        console.log('status', err?.response?.status)
        console.log('data', err?.response?.data)
        console.log('headers', err?.response?.headers)
        console.groupEnd()
      } catch {
        // ignore
      }
      await showError(
        isNew ? t('overtimeRequestPage.saveFailedTitle') : t('overtimeRequestPage.updateFailedTitle'),
        err?.response?.data?.message ?? err?.response?.data?.Message ?? err?.message ?? t('overtimeRequestPage.saveFailed'),
      )
    } finally {
      setSaving(false)
    }
  }

  if (!branchId) {
    return (
      <div className="space-y-6 p-6">
        <PageHeader title={t('overtimeRequestPage.pageTitle')} description={t('overtimeRequestPage.pageDescription')} />
        <Card>
          <CardContent className="p-6">
            <p className="text-muted-foreground">{t('overtimeRequestPage.selectBranchFirst')}</p>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6 p-6">
      <PageHeader title={t('overtimeRequestPage.pageTitle')} description={t('overtimeRequestPage.pageDescription')} />

      <form onSubmit={handleSubmit}>
        <Card>
          <CardContent className="p-6 space-y-6 overflow-visible">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="space-y-2">
                <Label>
                  {req} {t('overtimeRequestPage.labelDate')}
                </Label>
                <Input type="date" value={overtimeDate} onChange={(e) => setOvertimeDate(e.target.value)} disabled={saving || loading || readOnly} />
              </div>
              <div className="space-y-2">
                <Label>
                  {req} {t('overtimeRequestPage.labelTimeFrom')}
                </Label>
                <Input type="time" value={timeFrom} onChange={(e) => setTimeFrom(e.target.value)} disabled={saving || loading || readOnly} />
              </div>
              <div className="space-y-2">
                <Label>
                  {req} {t('overtimeRequestPage.labelTimeTo')}
                </Label>
                <Input type="time" value={timeTo} onChange={(e) => setTimeTo(e.target.value)} disabled={saving || loading || readOnly} />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 rounded-md border border-input bg-muted/30 p-4">
              <div>
                <p className="text-sm text-muted-foreground">{t('overtimeRequestPage.statTotalHours')}</p>
                <p className="text-lg font-semibold">{totalHours ? totalHours : 0}</p>
              </div>
              {!isNew ? (
                <div className="space-y-2">
                  <Label>{t('overtimeRequestPage.labelStatus')}</Label>
                  <select className="w-full h-10 rounded-md border border-input bg-background px-3 py-2 text-sm" value={String(status)} onChange={(e) => setStatus(Number(e.target.value) || 0)} disabled={saving || loading || readOnly}>
                    <option value="0">{t('overtimeRequestPage.placeholderAll')}</option>
                    {statusLookups.map((s) => {
                      const id = Number(s.lookupCodeId ?? s.id ?? 0)
                      if (!id) return null
                      const label = String(s.lookupNameEn ?? s.lookupNameAr ?? s.nameEn ?? s.nameAr ?? s.name ?? id).trim()
                      return (
                        <option key={id} value={String(id)}>
                          {label}
                        </option>
                      )
                    })}
                  </select>
                </div>
              ) : (
                <div className="md:col-span-2" />
              )}
              {!isNew ? (
                <div className="space-y-2">
                  <Label>{t('overtimeRequestPage.labelApprovedAt')}</Label>
                  <Input value={approvalDate} onChange={(e) => setApprovalDate(e.target.value)} disabled={saving || loading || readOnly} placeholder="YYYY-MM-DDTHH:mm:ss" />
                </div>
              ) : null}
            </div>

            <div className="space-y-2">
              <Label>
                {t('overtimeRequestPage.labelDetails')}
              </Label>
              <Textarea
                value={details}
                onChange={(e) => setDetails(e.target.value)}
                placeholder={t('overtimeRequestPage.placeholderDetails', { defaultValue: 'Details (optional)' })}
                disabled={saving || loading || readOnly}
                rows={4}
              />
            </div>

            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" disabled={saving} onClick={() => navigate('/self-services/overtime-requests')}>
                {t('overtimeRequestPage.return')}
              </Button>
              <Button type="submit" disabled={saving || loading || readOnly}>
                <Save className="h-4 w-4 mr-2" />
                {saving ? t('overtimeRequestPage.saving') : isNew ? t('overtimeRequestPage.save') : t('overtimeRequestPage.update')}
              </Button>
            </div>
          </CardContent>
        </Card>
      </form>
    </div>
  )
}

